import React, { useEffect, useState } from "react";
import { Link, Redirect } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowCircleLeft } from "@fortawesome/pro-regular-svg-icons/faArrowCircleLeft";

import { Helmet } from "react-helmet";

import dayjs from "dayjs";

import {
  Heading,
  Group,
  NotFoundMessage,
  UnauthorisedMessage,
} from "@peracto/peracto-ui";
import { useConfig } from "@peracto/peracto-config";

import { GET_ONE, useClient } from "@peracto/client";

const currencySymbols = {
  GBP: "£",
  USD: "$",
  EUR: "€",
};

export const formatAsCurrency = (price = 0, currency = "GBP") => {
  return `${currencySymbols[currency]}${(price / 100).toFixed(2)}`;
};

const DigitalReturnsViewContainer = ({ children }) => {
  return (
    <>
      <div className="form-container">
        <Heading name="View Return">
          <div className="flex-grow-1 d-flex align-items-center justify-content-end">
            <Link className="btn btn-outline-primary" to="/digital-returns">
              <FontAwesomeIcon icon={faArrowCircleLeft} className="mr-2" />
              Back to Returns
            </Link>
          </div>
        </Heading>
        {children}
      </div>
    </>
  );
};

export const DigitalReturnsView = ({ location: { pathname } }) => {
  const { client } = useClient();
  const [loading, setLoading] = useState(true);
  const [unauthorised, setUnauthorised] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [redirect, setRedirect] = useState();
  const [orderData, setOrderData] = useState();

  const config = useConfig();
  const { orders } = config.get("features", {});

  // show prices as tax feature flag - default: true
  const includeTax =
    orders && orders.hasOwnProperty("includeTax")
      ? orders.includeTax === true
      : true;

  const fetchOrder = async () => {
    try {
      const { data, response } = await client(GET_ONE, "orders", {
        id: pathname,
      });

      if (response.status === 404) {
        setRedirect("/orders");
        setLoading(false);
        return;
      }

      setOrderData(data);
      setLoading(false);
    } catch (e) {
      console.error(e);

      if (e.status === 403) {
        setUnauthorised(true);
      }

      if (e.status === 404) {
        setNotFound(true);
      }

      setRedirect("/orders");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <DigitalReturnsViewContainer>
        <div className="card">
          <div className="card-body">Loading...</div>
        </div>
      </DigitalReturnsViewContainer>
    );
  }

  if (unauthorised) {
    return <UnauthorisedMessage />;
  }

  if (notFound) {
    return (
      <NotFoundMessage
        url="/orders"
        message="The order you're looking for could not be found"
        buttonLabel="Go to Orders"
      />
    );
  }

  return (
    <DigitalReturnsViewContainer>
      {redirect ? (
        <Redirect to={redirect} />
      ) : (
        <>
          <Helmet>
            <title>
              {orderData.reference
                ? `Order #: ${orderData.reference}`
                : "Order"}{" "}
              | View | Peracto
            </title>
          </Helmet>
          {
            <>
              <Group
                key="order-details"
                id="order-details"
                name="Order Details"
              >
                <table className="table border-0">
                  <colgroup>
                    <col width="20%" />
                    <col width="80%" />
                  </colgroup>
                  <tbody>
                    <tr>
                      <td className="py-1 pl-1 font-bold border-top-0">
                        Order Number:
                      </td>
                      <td className="py-1 pr-1 border-top-0">
                        {orderData.orderNumber}
                      </td>
                    </tr>

                    <tr>
                      <td className="py-1 pl-1 font-bold border-top-0">
                        Email:
                      </td>
                      <td className="py-1 pr-1 border-top-0">
                        {orderData.email}
                      </td>
                    </tr>

                    <tr>
                      <td className="py-1 pl-1 font-bold border-top-0">
                        Customer Reference:
                      </td>
                      <td className="py-1 pr-1 border-top-0">
                        {orderData.customerReference}
                      </td>
                    </tr>

                    <tr>
                      <td className="py-1 pl-1 font-bold border-top-0">
                        Babbage Account:
                      </td>
                      <td className="py-1 pr-1 border-top-0">
                        {orderData.accountNumber}
                      </td>
                    </tr>

                    <tr>
                      <td className="py-1 pl-1 font-bold border-top-0">
                        Return Created:
                      </td>
                      <td className="py-1 pr-1 border-top-0">
                        {dayjs(new Date(orderData.createdAt)).format(
                          "YYYY-MM-DD HH:mm:ss"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Group>

              <Group key="items" id="items" name="Items">
                {/* Header */}
                <div className="grid grid-cols-12 gap-4 mb-4 font-bold">
                  <div className="col-span-2">SKU</div>
                  <div className="col-span-2 text-center">Quantity</div>
                  <div className="col-span-2">Reason</div>
                  <div className="col-span-4">Additional Information</div>
                  <div className="col-span-2 text-right">Unit Price</div>
                </div>

                {/* Items */}
                {orderData?.lines?.map((line, idx) => (
                  <div
                    key={`product_${idx}`}
                    className="grid grid-cols-12 gap-4 py-2 border-t"
                  >
                    <div className="col-span-2 font-bold">{line.sku}</div>
                    <div className="col-span-2 text-center">
                      {line.quantity}
                    </div>
                    <div className="col-span-2">{line.reason}</div>
                    <div className="col-span-4 break-words">
                      {line.additionalInformation}
                    </div>
                    <div className="col-span-2 text-right">
                      {formatAsCurrency(
                        includeTax
                          ? line?.unitPrice?.incTax
                          : line?.unitPrice?.excTax,
                        orderData?.currency
                      )}
                    </div>
                  </div>
                ))}
              </Group>
            </>
          }
        </>
      )}
    </DigitalReturnsViewContainer>
  );
};

export default DigitalReturnsView;
